import {
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { scrollToFirstInvalidControl } from '../../../../../common/utils/form-utils';
import { formatDescription } from '../../../../../common/utils/format-description';
import { AppIconComponent } from '../../../../../common/components/icon/icon.component';
import { lastValueFrom } from 'rxjs';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  InsuranceTypeDto,
  InsuranceCategory,
} from '../../../../../core/data/dtos/insurance-type.dto';
import { CommonModule } from '@angular/common';
import { InsuranceTypeCoverageDto } from '../../../../../core/data/dtos/insurance-type-coverage.dto';
import { InsuranceTypeCurrencyDto } from '../../../../../core/data/dtos/insurance-type-currency.dto';
import { ProducerDto } from '../../../../../core/data/dtos/producer.dto';
import { NgxMaskDirective } from 'ngx-mask';
import getSafeIconName from '../../../../../common/functions/get-safe-icon-name';
import { ProducersService } from '../../../services/producers.service';
import { OccupationsService } from '../../../services/occupations.service';
import { OccupationDto } from '../../../../../core/data/dtos/occupation.dto';
import { QuoteDetailCreateDto } from '../../../../../core/data/dtos/quote-detail-create.dto';
import { Gender } from '../../../../../core/data/enums/gender.enum';
import { EnfermedadesGravesPlanDto } from '../../../../../core/data/dtos/enfermedades-graves-plan.dto';
import { calcAgeYears, parseDateString, toDisplayDate } from '../../../../../common/utils/date-utils';
import { cmToFeetInches } from '../../../../../common/utils/quote-detail-mappers';
import {
  InsuranceQuestionDto,
  QuestionType,
} from '../../../../../core/data/dtos/insurance-question.dto';
import { CurrencySelectorComponent } from './components/currency-selector/currency-selector.component';
import { PrimaryInsuredComponent } from './components/primary-insured/primary-insured.component';
import { ChildrenDependentsComponent } from './components/children-dependents/children-dependents.component';
import { OtherDependentsComponent } from './components/other-dependents/other-dependents.component';
import { CoverageSelectorComponent } from './components/coverage-selector/coverage-selector.component';
import { QuestionsFormComponent } from './components/questions-form/questions-form.component';
import { ProducerSelectorComponent } from './components/producer-selector/producer-selector.component';
import { SpecialRequestModalComponent } from '../special-request-modal/special-request-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { KG_TO_LB, MAX_BMI, computeBmi as computeBmiFromImperial } from '../../../../../common/utils/bmi-validators';
import { ExchangeRateService } from '../../../../../core/data/services/exchange-rate.service';

type PopoverCtor = typeof import('bootstrap/js/dist/popover.js').default;
type PopoverInstance = import('bootstrap/js/dist/popover.js').default;

@Component({
  selector: 'app-option-modal',
  standalone: true,
  imports: [
    CommonModule,
    AppIconComponent,
    ReactiveFormsModule,
    NgxMaskDirective,
    CurrencySelectorComponent,
    PrimaryInsuredComponent,
    ChildrenDependentsComponent,
    OtherDependentsComponent,
    CoverageSelectorComponent,
    QuestionsFormComponent,
    ProducerSelectorComponent,
  ],
  templateUrl: './option-modal.component.html',
  styleUrl: './option-modal.component.css',
})
export class OptionModalComponent implements OnInit {
  @Input() selected: boolean = false;
  @Input() canDelete: boolean = true;
  @Input({ required: true }) option!: InsuranceTypeDto;
  @Input() intermediaryOptions: Array<{
    id: string;
    name: string;
    registration: string;
  }> = [];
  // Preloaded values when editing an already selected option
  @Input() initialDetail?: QuoteDetailCreateDto;
  @Input() initialProducerCode?: string | null;
  @Input() otherSelectedDetails: Array<{
    detail: any;
    insuranceType: InsuranceTypeDto;
  }> = [];
  @Input() currentGlobalCurrency?: string | null;
  @Input() initialCurrency?: string;
  @Input() disableQuestions: boolean = false;
  @ViewChildren('infoButton', { read: ElementRef }) infoButtons!: QueryList<
    ElementRef<HTMLButtonElement>
  >;
  form!: FormGroup;
  producers: ProducerDto[] = [];
  occupations: OccupationDto[] = [];
  readonly Gender = Gender;
  readonly InsuranceCategory = InsuranceCategory;
  /** Health questionnaire questions for the current category */
  categoryQuestions: InsuranceQuestionDto[] = [];
  currencyWarning: string | null = null;
  currencyError: string | null = null;
  coverageError: string | null = null;
  planError: string | null = null;
  intermediaryError: string | null = null;
  protected readonly getSafeIconName = getSafeIconName;
  private el = inject(ElementRef);
  private readonly exchangeRateService = inject(ExchangeRateService);
  private popoverCtor?: PopoverCtor;
  private readonly popovers = new Map<HTMLElement, PopoverInstance>();
  private previousCurrency: string = 'DOP';

  constructor(
    private readonly fb: FormBuilder,
    private readonly producersService: ProducersService,
    private readonly occupationsService: OccupationsService,
    public readonly activeModal: NgbActiveModal,
    private readonly modalService: NgbModal,
  ) { }

  get isEnfermedadesGraves(): boolean {
    return this.option?.formulaId === 'EnfermedadesGraves';
  }

  get isRespaldoColonial(): boolean {
    return this.option?.formulaId === 'RespaldoColonial';
  }

  get isUltimosGastos(): boolean {
    return this.option?.formulaId === 'UltimosGastos';
  }

  /** Returns true when the coverage has an insurePrimaryFraction > 0. */
  hasFraction(cov?: InsuranceTypeCoverageDto): boolean {
    return !!cov && typeof cov.insurePrimaryFraction === 'number' && cov.insurePrimaryFraction > 0;
  }

  /**
   * Computes the fraction-based amount for a coverage.
   * fraction is a percentage (e.g. 100 = 100%).
   * Base is the sum of required coverages (mainCoverage value).
   */
  computeFractionAmount(cov: InsuranceTypeCoverageDto): number {
    const fraction = cov.insurePrimaryFraction ?? 0;
    const mainValue = Number(this.form.get('mainCoverage')?.value ?? 0);
    return Math.round((fraction / 100) * mainValue * 100) / 100;
  }

  get sortedCoveragePlans(): any[] {
    const plans = this.option?.coveragePlans ?? [];
    return [...plans].sort(
      (a, b) => this.getPlanTotalPremium(a) - this.getPlanTotalPremium(b),
    );
  }

  getPlanTotalPremium(plan: any): number {
    if (!plan?.items?.length) return 0;
    const basePremium = plan.items.reduce((sum: number, item: any) => sum + (item.premium || 0), 0);
    return basePremium * this.insuredCount;
  }

  getPlanTotalSumInsured(plan: any): number {
    if (!plan?.items?.length) return 0;
    return plan.items.reduce((sum: number, item: any) => sum + (item.sumInsured || 0), 0);
  }

  private readonly coveragePriority: string[] = [
    'Muerte',
    'Cobertura de Vida',
    'Últimos Gastos',
    'Pago Alquiler Hogar',
    'Pago Alimenticio',
    'Pago Estudios',
    'Otros Gastos',
  ];

  sortedPlanItems(plan: any): any[] {
    const items = plan?.items ?? [];
    return [...items].sort((a, b) => {
      const sumDiff = Number(b?.sumInsured ?? 0) - Number(a?.sumInsured ?? 0);
      if (sumDiff !== 0) return sumDiff;
      const aIdx = this.coveragePriority.indexOf(a?.coverageName ?? '');
      const bIdx = this.coveragePriority.indexOf(b?.coverageName ?? '');
      const aPriority = aIdx === -1 ? this.coveragePriority.length : aIdx;
      const bPriority = bIdx === -1 ? this.coveragePriority.length : bIdx;
      return aPriority - bPriority;
    });
  }

  // Children helpers
  get childrenArray(): FormArray<FormGroup> {
    return this.form.get('children') as FormArray<FormGroup>;
  }

  // Others helpers
  get othersArray(): FormArray<FormGroup> {
    return this.form.get('others') as FormArray<FormGroup>;
  }

  // Helpers for dynamic data
  get availableCurrencies(): string[] {
    const list = this.option?.currencies ?? [];
    const codes = (list || [])
      .map((c) => c?.currency || '')
      .filter((v): v is string => !!v);
    // unique preserve order
    let unique = Array.from(new Set(codes));

    // Filter out USD if exchange rate is not available
    if (!this.exchangeRateService.isUsdAvailable()) {
      unique = unique.filter((c) => c.toUpperCase() !== 'USD');
    }

    // If no currencies defined, treat as DOP
    return unique.length > 0 ? unique : ['DOP'];
  }

  get primaryCoverage(): InsuranceTypeCoverageDto | undefined {
    // First required coverage (previously isPrimary) whose amount isn't a percentage of another one
    return (this.option?.coverages ?? [])?.find(
      (c) => (!!c?.isRequired || !!c?.isPrimary) && !this.hasFraction(c),
    );
  }

  get hasNoPrimaryCoverage(): boolean {
    return !this.primaryCoverage;
  }

  get additionalCoverages(): InsuranceTypeCoverageDto[] {
    // Required (non-primary) or optional coverages that are NOT calculated
    const primary = this.primaryCoverage;
    return (this.option?.coverages ?? []).filter(
      (c) => c !== primary && !c?.isCalculated,
    );
  }

  get calculatedCoverages(): InsuranceTypeCoverageDto[] {
    // Coverages where the limit is calculated at runtime (e.g., Supervivencia)
    return (this.option?.coverages ?? []).filter((c) => !!c?.isCalculated);
  }

  get insuredCount(): number {
    let count = 1; // primary insured
    if (this.form?.get('spouseIncluded')?.value) count++;
    if (this.form?.get('childrenEnabled')?.value) {
      count += this.childrenArray?.length ?? 0;
    }
    if (this.form?.get('othersEnabled')?.value) {
      count += this.othersArray?.length ?? 0;
    }
    return count;
  }

  /**
   * Finds the premium amount for a plan based on the primary insured's age.
   * Looks through the plan's premiums to find one where age is between minAge and maxAge.
   */
  getPlanPremium(plan: EnfermedadesGravesPlanDto): number | null {
    const birthDateStr = this.form?.get('birthDate')?.value as string | null;
    if (!birthDateStr || !plan.premiums?.length) return null;
    const age = this.calcAgeYears(birthDateStr);
    if (age == null) return null;
    const premium = plan.premiums.find(p => age >= p.minAge && age <= p.maxAge);
    return premium?.amount ?? null;
  }

  get hasDurationConstraints(): boolean {
    const min = this.option?.durationMin;
    const max = this.option?.durationMax;
    // Show duration section only when there are explicit min or max duration values
    return min != null || max != null;
  }

  ngOnInit(): void {
    // Deep copy otherSelectedDetails to avoid mutating parent state before saving
    if (this.otherSelectedDetails) {
      this.otherSelectedDetails = JSON.parse(JSON.stringify(this.otherSelectedDetails));
    }

    // Build base form with constraints from option
    // durationMin/Max are in years in data. Convert to months for form logic.
    const durationMinYears = this.option.durationMin ?? 1;
    const durationMaxYears = this.option.durationMax ?? 120;
    const durationMinMonths = durationMinYears * 12;
    const durationMaxMonths = durationMaxYears * 12;

    // Only require duration when there are actual constraints to display
    const durationValidators = this.hasDurationConstraints
      ? [
        Validators.required,
        Validators.min(durationMinMonths),
        Validators.max(durationMaxMonths),
      ]
      : [];

    const defaultCurrency = this.initialCurrency || (this.availableCurrencies[0] ?? 'DOP');
    this.previousCurrency = defaultCurrency;

    this.form = this.fb.group(
      {
        duration: [null, durationValidators],
        birthDate: [null],
        currency: [defaultCurrency, []],
        // Primary coverage amount
        mainCoverage: [null, []],
        // Family section controls
        primaryGender: [null, [Validators.required]],
        // Body Mass Index inputs for the primary insured
        weight: [null, [Validators.required, Validators.min(1)]],
        weightUnit: ['lb'],
        heightFeet: [null, [Validators.required, Validators.min(0), Validators.max(8)]],
        heightInches: [null, [Validators.required, Validators.min(0), Validators.max(11)]],
        spouseIncluded: [false],
        spouseBirthDate: [{ value: null, disabled: true }],
        spouseGender: [null],
        childrenEnabled: [false],
        children: this.fb.array([]),
        othersEnabled: [false],
        others: this.fb.array([]),
        // Intermediary
        intermediaryEnabled: [false],
        intermediary: [null],
        // Plan selection for Enfermedades Graves, Respaldo Colonial and Ultimos Gastos
        planOption: [
          null,
          (this.isEnfermedadesGraves || this.isRespaldoColonial || this.isUltimosGastos) ? [Validators.required] : [],
        ],
        durationYears: [null],
      },
      { validators: [this.maxDurationAgeValidator.bind(this), this.bmiValidator.bind(this)] },
    );

    // Add control-level validator for primary insured birth date so the input can show inline errors
    const birthDateCtrl = this.form.get('birthDate');
    if (birthDateCtrl) {
      birthDateCtrl.setValidators([
        Validators.required,
        (control: AbstractControl) => this.validatePrimaryBirthDate(control),
      ]);
      birthDateCtrl.updateValueAndValidity({ emitEvent: false });

      // Re-validate birth date when duration changes because maxDurationAge depends on it
      this.form.get('duration')?.valueChanges.subscribe(() => {
        this.revalidateAgeDependentControls();
      });

      birthDateCtrl.valueChanges.subscribe(() => {
        // Re-validate children birth dates because of the 13-year difference rule
        const children = this.childrenArray;
        children?.controls?.forEach((grp) =>
          grp.get('birthDate')?.updateValueAndValidity({ emitEvent: false }),
        );
        this.applyCoverageAgeConstraints();
        // The primary coverage maximum can depend on the age (age-band limits)
        if (this.option?.ageAmountLimits?.length) {
          this.applyCoverageValidators();
        }
      });
    }

    // Dynamic spouseAge enable/validators based on spouseIncluded
    this.form
      .get('spouseIncluded')
      ?.valueChanges.subscribe((included: boolean) => {
        const bdCtrl = this.form.get('spouseBirthDate');
        const spouseGenderCtrl = this.form.get('spouseGender');
        if (bdCtrl) {
          if (included) {
            bdCtrl.enable({ emitEvent: false });
            bdCtrl.setValidators([
              Validators.required,
              (c: AbstractControl) => this.validateSpouseBirthDate(c),
            ]);
            spouseGenderCtrl?.setValidators([Validators.required]);
          } else {
            bdCtrl.reset(null);
            bdCtrl.clearValidators();
            bdCtrl.disable({ emitEvent: false });
            spouseGenderCtrl?.reset(null, { emitEvent: false });
            spouseGenderCtrl?.clearValidators();
          }
        }
        bdCtrl?.updateValueAndValidity({ emitEvent: false });
        spouseGenderCtrl?.updateValueAndValidity({ emitEvent: false });
        const children = this.childrenArray;
        children?.controls?.forEach((grp) =>
          grp.get('birthDate')?.updateValueAndValidity({ emitEvent: false }),
        );
      });

    this.form.get('spouseBirthDate')?.valueChanges.subscribe(() => {
      const children = this.childrenArray;
      children?.controls?.forEach((grp) =>
        grp.get('birthDate')?.updateValueAndValidity({ emitEvent: false }),
      );
    });

    // Duration years sync
    this.form.get('durationYears')?.valueChanges.subscribe((years) => {
      if (years === null || years === '') {
        this.form.get('duration')?.setValue(null, { emitEvent: false });
      } else {
        const y = Number(years);
        if (!isNaN(y)) {
          this.form.get('duration')?.setValue(y * 12, { emitEvent: false });
        }
      }
      // duration's own valueChanges won't fire (emitEvent: false above), so
      // trigger the age-dependent revalidation directly from here too
      this.revalidateAgeDependentControls();
    });

    this.form.get('duration')?.valueChanges.subscribe((months) => {
      if (months === null) {
        this.form.get('durationYears')?.setValue(null, { emitEvent: false });
        return;
      }
      const m = Number(months);
      if (!isNaN(m)) {
        const y = Math.ceil(m / 12);
        if (this.form.get('durationYears')?.value != y) {
          this.form.get('durationYears')?.setValue(y, { emitEvent: false });
        }
      }
    });

    // Children enabled toggle
    this.form
      .get('childrenEnabled')
      ?.valueChanges.subscribe((enabled: boolean) => {
        const arr = this.childrenArray;
        if (enabled) {
          if (arr.length === 0) {
            this.addChild();
          }
        } else {
          while (arr.length) arr.removeAt(0);
        }
      });

    // Others enabled toggle
    this.form
      .get('othersEnabled')
      ?.valueChanges.subscribe((enabled: boolean) => {
        const arr = this.othersArray;
        if (enabled) {
          if (arr.length === 0) {
            this.addOther();
          }
        } else {
          while (arr.length) arr.removeAt(0);
        }
      });

    // Create dynamic controls for each additional coverage (checkbox + amount)
    this.additionalCoverages.forEach((cov) => {
      const isRequired = !!cov.isRequired;
      // If required, pre-check and disable the checkbox so it can't be unchecked
      this.form.addControl(
        this.selectedKey(cov),
        this.fb.control({ value: isRequired, disabled: isRequired }),
      );

      // Prefill amount with min (based on current currency) if available
      const currentCode = (this.form.get('currency')?.value as string) || '';
      const covRange = this.findCurrencyRange(cov?.currencies, currentCode);
      // Initialize with 0 if not required; required ones start at their fraction of the main amount or at the minimum
      const initialAmount = !isRequired
        ? 0
        : this.hasFraction(cov)
          ? this.computeFractionAmount(cov)
          : (covRange?.minAmount ?? covRange?.min ?? 0);

      this.form.addControl(
        this.amountKey(cov),
        this.fb.control({ value: initialAmount, disabled: !isRequired }),
      );

      // Enable/disable amount based on selection and set to min on enable
      this.form
        .get(this.selectedKey(cov))
        ?.valueChanges.subscribe((checked: boolean) => {
          const amountCtrl = this.form.get(this.amountKey(cov));
          if (!amountCtrl) return;
          if (checked) {
            if (this.hasFraction(cov)) {
              // Fraction-based: auto-calculate and keep readonly
              const computed = this.computeFractionAmount(cov);
              amountCtrl.setValue(computed, { emitEvent: false });
              amountCtrl.enable({ emitEvent: false });
            } else {
              // Ensure at least min when enabling (only if applicable)
              const code = (this.form.get('currency')?.value as string) || '';
              const range = this.findCurrencyRange(cov?.currencies, code);
              if (range && typeof range.minAmount === 'number') {
                const minAmount = range.minAmount;
                const current = Number(amountCtrl.value ?? 0);
                if (!current || current < minAmount) {
                  amountCtrl.setValue(minAmount, { emitEvent: false });
                }
              }
              amountCtrl.enable({ emitEvent: false });
            }
          } else {
            amountCtrl.setValue(0, { emitEvent: false });
            amountCtrl.setErrors(null);
            amountCtrl.disable({ emitEvent: false });
          }
          this.applyAdditionalCoverageValidators();
        });
    });

    // Apply validators based on selected currency
    this.applyCoverageValidators();

    // Re-apply additional coverage validators when mainCoverage changes so their
    // max is updated to the new primary value (do NOT re-run primary coverage
    // auto-clamping here — that would fight the user's input)
    this.form.get('mainCoverage')?.valueChanges.subscribe(() => {
      this.applyAdditionalCoverageValidators();
      // Update fraction-based coverage amounts when primary amount changes
      this.additionalCoverages.forEach((cov) => {
        if (!this.hasFraction(cov)) return;
        const selCtrl = this.form.get(this.selectedKey(cov));
        if (!selCtrl?.value) return;
        const amountCtrl = this.form.get(this.amountKey(cov));
        if (!amountCtrl) return;
        const computed = this.computeFractionAmount(cov);
        amountCtrl.setValue(computed, { emitEvent: false });
      });
    });

    // Currency constraints & validation
    if (this.option.currencies?.length) {
      const currencyCtrl = this.form.get('currency');
      // Set initial value based on priority: explicit initial -> form value -> first available
      if (!currencyCtrl?.value) {
        currencyCtrl?.setValue(this.option.currencies[0].currency);
      }

      // Validates currency compatibility
      currencyCtrl?.valueChanges.subscribe((val) => {
        this.handleCurrencyChange(val);
        this.validateCurrencyChange(val);
        // Refresh validators when currency changes
        this.applyCoverageValidators();
      });
    }
    this.form
      .get('intermediaryEnabled')
      ?.valueChanges.subscribe((enabled: boolean) => {
        if (!enabled) {
          this.form.get('intermediary')?.setValue(null, { emitEvent: false });
          this.intermediaryError = null;
        }
      });

    // Load producers for preferred intermediary selection
    void this.loadProducers();

    // Occupations if required
    if (this.option?.requiresOccupation) {
      this.form.addControl(
        'occupationCode',
        this.fb.control(null, [Validators.required]),
      );
      void this.loadOccupations();
    }

    // Health questionnaire initialization
    const groups = this.option?.questionGroups || [];
    const selectionGroups = groups.filter(
      (g) => g.phase === 'Selection' || !g.phase,
    );

    this.categoryQuestions = [];
    selectionGroups.forEach((g) => {
      if (g.questions) {
        this.categoryQuestions.push(...g.questions);
      }
    });

    this.categoryQuestions.forEach((q) => {
      // Start with null so no option is pre-selected; user must choose explicitly
      const init: any = null;
      // Custom required validator that accepts `false` as a valid answer (Validators.required rejects falsy values)
      const boolRequired = (ctrl: AbstractControl) => (ctrl.value === null || ctrl.value === undefined) ? { required: true } : null;
      const validators = q.type === QuestionType.Bool ? [boolRequired] : [];
      const ctrl = this.fb.control(init, validators);
      if (this.disableQuestions) {
        ctrl.disable();
      }
      this.form.addControl(this.questionKey(q), ctrl);
    });

    // After base init, preload saved information if provided
    this.preloadFromInitialDetail();

    // Popovers after init
    setTimeout(() => void this.ensurePopovers());
  }

  ngOnDestroy(): void {
    this.detachPopovers();
  }

  addChild(): void {
    this.childrenArray.push(this.newChildGroup());
  }

  removeChild(index: number): void {
    this.childrenArray.removeAt(index);
    if (this.childrenArray.length === 0) {
      this.form.get('childrenEnabled')?.setValue(false, { emitEvent: true });
    }
  }

  addOther(): void {
    this.othersArray.push(this.newOtherGroup());
  }

  removeOther(index: number): void {
    this.othersArray.removeAt(index);
    if (this.othersArray.length === 0) {
      this.form.get('othersEnabled')?.setValue(false, { emitEvent: true });
    }
  }

  public getCoverageRange(
    cov?: InsuranceTypeCoverageDto,
  ): { min?: number; max?: number; minAmount?: number; maxAmount?: number } | null {
    const code = (this.form.get('currency')?.value as string) || '';
    // Default: use coverage-level limits for selected currency
    const covRange = this.findCurrencyRange(cov?.currencies, code);

    // For primary/required coverage, fall back to insurance-type currency limits when coverage-level limits are absent per field
    if (cov?.isPrimary || cov?.isRequired) {
      const typeRange = this.findCurrencyRange(this.option?.currencies, code);
      const merged: { min?: number; max?: number; minAmount?: number; maxAmount?: number } = {};

      if (covRange?.minAmount != null) merged.minAmount = covRange.minAmount;
      else if (typeRange?.minAmount != null) merged.minAmount = typeRange.minAmount;

      if (covRange?.maxAmount != null) merged.maxAmount = covRange.maxAmount;
      else if (typeRange?.maxAmount != null) merged.maxAmount = typeRange.maxAmount;

      // Primary coverages without a fraction are also capped by the age-band maximum.
      const ageCap = this.hasFraction(cov) ? null : this.getAgeAmountCap(code);
      if (ageCap != null && (merged.maxAmount == null || ageCap < merged.maxAmount)) {
        merged.maxAmount = ageCap;
      }

      return Object.keys(merged).length ? merged : null;
    }

    return covRange;
  }

  public selectedKey(cov: InsuranceTypeCoverageDto): string {
    return `selected_${cov.id ?? cov.name ?? ''}`;
  }

  public amountKey(cov: InsuranceTypeCoverageDto): string {
    return `amount_${cov.id ?? cov.name ?? ''}`;
  }

  public questionKey(q: InsuranceQuestionDto): string {
    return `question_${q.id}`;
  }

  // UI events
  onInfoClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget as HTMLButtonElement | null;
    this.hideOpenPopovers(button ?? undefined);
    button?.focus();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as Node | null;
    if (!target) return;

    // Keep popover open when clicking its trigger button or inside the popover itself
    const clickedTrigger = Array.from(this.popovers.keys()).some((el) =>
      el.contains(target),
    );
    const clickedPopover = (target as HTMLElement).closest('.popover');
    if (clickedTrigger || clickedPopover) return;

    this.hideOpenPopovers();
  }

  save(): void {
    // Reset custom errors
    this.coverageError = null;
    this.planError = null;
    this.intermediaryError = null;

    // Check for unmapped occupation (Contact Us flow)
    if (this.option?.requiresOccupation) {
      const occCode = this.form.get('occupationCode')?.value;
      if (occCode) {
        const occ = this.occupations.find((o) => o.occupationCode == occCode);
        // If occupation exists but has no category (null/undefined/0), show Contact Us
        if (occ && !occ.category) {
          const ref = this.modalService.open(SpecialRequestModalComponent, {
            centered: true,
          });
          ref.componentInstance.affectedInsurances = [
            {
              name: this.option.name,
              questions: [{ text: 'La ocupación seleccionada requiere revisión manual.', answer: true }],
            },
          ];

          ref.result.then(
            (data) => {
              if (data) {
                // User submitted contact info
                // Just close the option modal effectively cancelling the flow for now
                this.activeModal.dismiss('special-request');
              }
            },
            () => { },
          );
          return;
        }
      }
    }

    // If no primary coverage exists, user must select at least one additional coverage with a limit
    if (this.hasNoPrimaryCoverage && !this.isEnfermedadesGraves && !this.isRespaldoColonial && !this.isUltimosGastos) {
      const hasAnyCoverage = this.additionalCoverages.some((cov) => {
        const selected = this.form.get(this.selectedKey(cov))?.value;
        const amount = Number(this.form.get(this.amountKey(cov))?.value ?? 0);
        return !!selected && amount > 0;
      });
      if (!hasAnyCoverage) {
        this.coverageError = 'Debe seleccionar al menos una cobertura y especificar un límite asegurado.';
        this.form.markAllAsTouched();
        scrollToFirstInvalidControl(this.el);
        return;
      }
    }

    // Validate additional coverages do not exceed the main coverage value
    if (!this.hasNoPrimaryCoverage && !this.isEnfermedadesGraves) {
      const mainValue = Number(this.form.get('mainCoverage')?.value ?? 0);
      if (mainValue > 0) {
        const exceeds = this.additionalCoverages.some((cov) => {
          const selected = this.form.get(this.selectedKey(cov))?.value;
          const amount = Number(this.form.get(this.amountKey(cov))?.value ?? 0);
          return !!selected && amount > mainValue;
        });
        if (exceeds) {
          this.coverageError = 'El valor asegurado de las coberturas adicionales no puede superar el monto de la cobertura principal.';
          this.form.markAllAsTouched();
          scrollToFirstInvalidControl(this.el);
          return;
        }
      }
    }

    // For Enfermedades Graves, validate plan selection
    if (this.isEnfermedadesGraves && this.option.seriousIllnessPlans?.length) {
      const planValue = this.form.get('planOption')?.value;
      if (!planValue) {
        this.planError = 'Debe seleccionar un plan para continuar.';
        this.form.markAllAsTouched();
        scrollToFirstInvalidControl(this.el);
        return;
      }
    }

    // For Respaldo Colonial and Ultimos Gastos, validate plan selection
    if ((this.isRespaldoColonial || this.isUltimosGastos) && this.option.coveragePlans?.length) {
      const planValue = this.form.get('planOption')?.value;
      if (!planValue) {
        this.planError = 'Debe seleccionar un plan para continuar.';
        this.form.markAllAsTouched();
        scrollToFirstInvalidControl(this.el);
        return;
      }
    }

    // Validate intermediary selection: if toggle is on, an intermediary must be chosen
    if (this.form.get('intermediaryEnabled')?.value && !this.form.get('intermediary')?.value) {
      this.intermediaryError = 'Debe seleccionar un intermediario o desactivar la opción de intermediario para continuar.';
      this.form.markAllAsTouched();
      scrollToFirstInvalidControl(this.el);
      return;
    }

    if (this.form.invalid) {
      // Debug: log which controls are invalid
      const invalidControls: string[] = [];
      Object.keys(this.form.controls).forEach(key => {
        const ctrl = this.form.get(key);
        if (ctrl?.invalid) {
          invalidControls.push(`${key}: ${JSON.stringify(ctrl.errors)}`);
        }
      });
      if (this.form.errors) {
        invalidControls.push(`FORM-LEVEL: ${JSON.stringify(this.form.errors)}`);
      }
      console.warn('Invalid controls:', invalidControls);

      this.form.markAllAsTouched();
      scrollToFirstInvalidControl(this.el);
      return;
    }
    const val = this.form.getRawValue();
    this.activeModal.close({
      option: this.option,
      formValue: val,
      otherSelectedDetails: this.otherSelectedDetails,
    });
  }

  cancel(): void {
    this.activeModal.dismiss('cancel');
  }

  removeSelected(): void {
    this.activeModal.dismiss('remove');
  }

  private preloadFromInitialDetail(): void {
    const detail = this.initialDetail;
    if (!detail) return;

    // 1) Currency first (if provided and supported)
    let curr = (detail.currency || undefined) as string | undefined;
    if (!curr && (detail.coverages || []).length) {
      curr = (detail.coverages![0].currency || undefined) as string | undefined;
    }
    if (curr && this.availableCurrencies.includes(curr)) {
      this.form.get('currency')?.setValue(curr, { emitEvent: true });
    }

    // Ensure validators reflect currency
    this.applyCoverageValidators();

    // 2) Duration: map years back to months
    const years = Number(detail.durationYears ?? 0);
    if (years > 0) {
      const months = years * 12;
      const durationCtrl = this.form.get('duration');
      const minDYears = this.option.durationMin ?? 1;
      const maxDYears = this.option.durationMax ?? 120;
      const minD = minDYears * 12;
      const maxD = maxDYears * 12;
      const clamped = Math.min(Math.max(months, minD), maxD);
      durationCtrl?.setValue(clamped, { emitEvent: false });
      this.form
        .get('durationYears')
        ?.setValue(clamped / 12, { emitEvent: false });
    }

    // 3) Intermediary
    const code = this.initialProducerCode;
    if (code) {
      this.form
        .get('intermediaryEnabled')
        ?.setValue(true, { emitEvent: false });
      // Convert to number to match ng-select bindValue (ProducerDto.producerCode is number)
      const numericCode = Number(code);
      this.form.get('intermediary')?.setValue(isNaN(numericCode) ? code : numericCode, { emitEvent: false });
    }

    // 4) Coverages: main + additional
    const primary = this.primaryCoverage;
    const detailCoverages = detail.coverages || [];
    if (primary) {
      const main = detailCoverages.find((c: any) => {
        // Prefer matching by coverageId or coverageCode, fallback to name when missing
        if (primary.id != null && c.coverageId != null)
          return c.coverageId === primary.id;
        if (primary.coverageCode != null && c.coverageCode != null)
          return c.coverageCode === primary.coverageCode;
        return (
          (c.name || '').toLowerCase() === (primary.name || '').toLowerCase()
        );
      });
      if (main && typeof (main as any).amount === 'number') {
        const mainCtrl = this.form.get('mainCoverage');
        // Angular mask is applied; control stores number or string with commas: set raw number
        const range = this.getCoverageRange(primary);
        const min = range?.min ?? 0;
        const value = Math.max(Number((main as any).amount || 0), min);
        mainCtrl?.setValue(value, { emitEvent: false });
      }
    }

    // Additional coverages mapping by coverageId, coverageCode or name
    this.additionalCoverages.forEach((cov) => {
      const found = (detailCoverages as any[]).find((c: any) => {
        if (cov.id != null && c.coverageId != null)
          return c.coverageId === cov.id;
        if (cov.coverageCode != null && c.coverageCode != null)
          return c.coverageCode === cov.coverageCode;
        return (c.name || '').toLowerCase() === (cov.name || '').toLowerCase();
      });
      const selKey = this.selectedKey(cov);
      const amtKey = this.amountKey(cov);
      if (found && typeof (found as any).amount === 'number') {
        this.form.get(selKey)?.setValue(true, { emitEvent: false });
        const range = this.getCoverageRange(cov);
        const min = range?.min ?? 0;
        const value = Math.max(Number((found as any).amount || 0), min);
        const amountCtrl = this.form.get(amtKey);
        amountCtrl?.setValue(value, { emitEvent: false });
        amountCtrl?.enable({ emitEvent: false });
      } else {
        // Ensure unchecked state remains with control disabled and value 0
        this.form.get(selKey)?.setValue(false, { emitEvent: false });
        const amountCtrl = this.form.get(amtKey);
        amountCtrl?.setValue(0, { emitEvent: false });
        amountCtrl?.disable({ emitEvent: false });
      }
    });

    // Family and occupations prefill from existing insuredPeople
    const ips = (detail.insuredPeople || []) as any[];

    // Primary: birthDate, gender and occupation (if required)
    const primaryInsured = ips.find((ip) => !!ip?.isPrimary);
    const primaryBd = primaryInsured?.person?.birthDate ?? null;
    if (primaryBd) {
      this.form.get('birthDate')?.setValue(toDisplayDate(primaryBd), { emitEvent: false });
    }
    const primaryGender = (primaryInsured?.person as any)?.gender;
    if (primaryGender != null) {
      this.form
        .get('primaryGender')
        ?.setValue(primaryGender, { emitEvent: false });
    }
    if (this.option?.requiresOccupation) {
      const occ = (primaryInsured?.person as any)?.occupation;
      if (occ != null) {
        const num = Number(occ);
        if (!Number.isNaN(num)) {
          this.form.get('occupationCode')?.setValue(num, { emitEvent: false });
        }
      }
    }

    // Height and weight are stored on the person as cm / lb (0 when never entered).
    const { feet, inches } = cmToFeetInches(primaryInsured?.person?.heightCm);
    if (feet != null) {
      this.form.get('heightFeet')?.setValue(feet, { emitEvent: false });
      this.form.get('heightInches')?.setValue(inches ?? 0, { emitEvent: false });
    }
    const weightLb = Number(primaryInsured?.person?.weightLb);
    if (Number.isFinite(weightLb) && weightLb > 0) {
      this.form.get('weight')?.setValue(weightLb, { emitEvent: false });
      this.form.get('weightUnit')?.setValue('lb', { emitEvent: false });
    }
    if (feet != null || weightLb > 0) {
      // Re-run the group validators (BMI) now that the values are back.
      this.form.updateValueAndValidity({ emitEvent: false });
    }

    const normalizeRelation = (rel: any): 'spouse' | 'child' | 'other' | null => {
      if (rel == null) return null;
      const r = String(rel).toLowerCase();
      if (['conyuge', 'cónyuge', 'conyuge', 'spouse'].includes(r))
        return 'spouse';
      if (['child', 'hijo', 'hija', 'hijx', 'niño', 'niña'].includes(r))
        return 'child';
      if (['madre', 'padre', 'hermano', 'hermana', 'abuelo', 'abuela',
        'nieto', 'nieta', 'primo', 'prima', 'tio', 'tía', 'tia',
        'sobrino', 'sobrina', 'other'].includes(r))
        return 'other';
      return null;
    };

    if (this.option?.familyAllowed && ips.length) {
      // Spouse
      const spouse = ips.find(
        (ip) =>
          !ip?.isPrimary &&
          normalizeRelation(ip?.person?.primaryInsuredRelation ?? (ip?.person as any)?.relationship) === 'spouse',
      );
      if (spouse) {
        this.form.get('spouseIncluded')?.setValue(true, { emitEvent: true });
        // gender may come as enum number or string
        const g = spouse.person?.gender;
        this.form
          .get('spouseGender')
          ?.setValue(g ?? this.form.get('spouseGender')?.value, {
            emitEvent: false,
          });
        const sb = spouse.person?.birthDate ?? null;
        if (sb) {
          this.form.get('spouseBirthDate')?.setValue(toDisplayDate(sb), { emitEvent: false });
        }
      }

      // Children
      const children = ips.filter(
        (ip) =>
          !ip?.isPrimary &&
          normalizeRelation(ip?.person?.primaryInsuredRelation ?? (ip?.person as any)?.relationship) === 'child',
      );
      if (children.length) {
        this.form.get('childrenEnabled')?.setValue(true, { emitEvent: true });
        // Ensure array size and patch values
        const arr = this.childrenArray;
        while (arr.length) arr.removeAt(0);
        children.forEach((c) => {
          const grp = this.newChildGroup();
          // gender
          grp
            .get('gender')
            ?.setValue(c?.person?.gender ?? grp.get('gender')?.value, {
              emitEvent: false,
            });
          // birthDate
          if (c?.person?.birthDate) {
            grp.get('birthDate')?.setValue(toDisplayDate(c.person.birthDate), {
              emitEvent: false,
            });
          }
          arr.push(grp);
        });
      }

      // Others (extended family)
      const others = ips.filter(
        (ip) =>
          !ip?.isPrimary &&
          normalizeRelation(ip?.person?.primaryInsuredRelation ?? (ip?.person as any)?.relationship) === 'other',
      );
      if (others.length) {
        this.form.get('othersEnabled')?.setValue(true, { emitEvent: true });
        const arr = this.othersArray;
        while (arr.length) arr.removeAt(0);
        others.forEach((o) => {
          const grp = this.newOtherGroup();
          grp
            .get('gender')
            ?.setValue(o?.person?.gender ?? grp.get('gender')?.value, {
              emitEvent: false,
            });
          grp
            .get('relationship')
            ?.setValue(
              o?.person?.primaryInsuredRelation ?? (o?.person as any)?.relationship ?? null,
              { emitEvent: false },
            );
          if (o?.person?.birthDate) {
            grp.get('birthDate')?.setValue(toDisplayDate(o.person.birthDate), {
              emitEvent: false,
            });
          }
          arr.push(grp);
        });
      }
    }

    // Health questionnaire questions
    const answers = detail.answers || [];
    if (answers.length > 0) {
      this.categoryQuestions.forEach((q) => {
        const match = answers.find((a) => a.questionId === q.id);
        if (match) {
          let val: any = match.value;
          if (q.type === QuestionType.Bool) {
            val = val === 'true' || val === true;
          }
          this.form
            .get(this.questionKey(q))
            ?.setValue(val, { emitEvent: false });
        }
      });
    }

    // Plan option for Enfermedades Graves
    if (this.isEnfermedadesGraves) {
      let planVal: string | null = detail.planOption ?? null;

      // Prioritize lookup by selectedPlanId if available
      if (!planVal && detail.selectedPlanId && this.option.seriousIllnessPlans?.length) {
        const matchedPlan = this.option.seriousIllnessPlans.find(p => p.id === detail.selectedPlanId);
        if (matchedPlan) {
          planVal = matchedPlan.name;
        }
      }

      // Fallback: look for plan name in coverages if not explicitly in planOption
      if (!planVal && this.option.seriousIllnessPlans?.length && detail.coverages?.length) {
        const plans = this.option.seriousIllnessPlans;
        // Find coverage whose name matches a plan name (case-insensitive)
        const foundCov = detail.coverages.find(c =>
          c.name && plans.some(p => p.name.toLowerCase() === c.name!.toLowerCase())
        );

        if (foundCov?.name) {
          const matchedPlan = plans.find(p => p.name.toLowerCase() === foundCov.name!.toLowerCase());
          planVal = matchedPlan?.name ?? foundCov.name;
        }
      }

      if (planVal) {
        this.form.get('planOption')?.setValue(planVal, { emitEvent: false });
      }
    }

    // Plan option for Respaldo Colonial and Ultimos Gastos
    if (this.isRespaldoColonial || this.isUltimosGastos) {
      let planVal: string | null = detail.planOption ?? null;

      // Prioritize lookup by selectedPlanId if available
      if (!planVal && detail.selectedPlanId && this.option.coveragePlans?.length) {
        const matchedPlan = this.option.coveragePlans.find(p => p.id === detail.selectedPlanId);
        if (matchedPlan) {
          planVal = matchedPlan.name;
        }
      }

      // Fallback: match coverage item names against plan item names
      if (!planVal && this.option.coveragePlans?.length && detail.coverages?.length) {
        const covNames = new Set(detail.coverages.map(c => (c.name || '').toLowerCase()).filter(n => n));
        const matchedPlan = this.option.coveragePlans.find(p =>
          p.items?.some((item: any) => covNames.has((item.coverageName || '').toLowerCase()))
        );
        if (matchedPlan) {
          planVal = matchedPlan.name;
        }
      }

      if (planVal) {
        this.form.get('planOption')?.setValue(planVal, { emitEvent: false });
      }
    }

    // Re-apply validators after patching values to enforce constraints without overriding
    this.applyCoverageValidators();
    this.applyCoverageAgeConstraints();
  }

  private async loadProducers(): Promise<void> {
    try {
      const list = await lastValueFrom(
        this.producersService.getProducers(this.option?.slob),
      );
      this.producers = list.filter(p => p.producerName !== null) ?? [];
    } catch {
      this.producers = [];
    }

    // Re-apply the intermediary value now that producers are loaded,
    // so ng-select can match it against the populated items list.
    const currentValue = this.form.get('intermediary')?.value;
    if (currentValue != null) {
      this.form.get('intermediary')?.setValue(currentValue);
    }
  }

  private async loadOccupations(): Promise<void> {
    try {
      const list = await lastValueFrom(
        this.occupationsService.getOccupations(),
      );
      this.occupations = list ?? [];
    } catch {
      this.occupations = [];
    }
  }

  private newChildGroup(): FormGroup {
    const grp = this.fb.group({
      birthDate: [
        null,
        [
          Validators.required,
          (c: AbstractControl) => this.validateChildBirthDate(c),
        ],
      ],
      gender: [null, [Validators.required]],
    });
    return grp;
  }

  private newOtherGroup(): FormGroup {
    const grp = this.fb.group({
      relationship: [null, [Validators.required]],
      birthDate: [
        null,
        [
          Validators.required,
          (c: AbstractControl) => this.validateOtherBirthDate(c),
        ],
      ],
      gender: [null, [Validators.required]],
    });
    return grp;
  }

  public isCoverageFixedLimit(cov?: InsuranceTypeCoverageDto): boolean {
    if (!cov || !cov.currencies) return false;
    const code = (this.form.get('currency')?.value as string) || '';
    const found = (cov.currencies ?? []).find(
      (c) => (c?.currency || '').toUpperCase() === code.toUpperCase(),
    );
    return found?.isFixedLimit ?? false;
  }

  /**
   * Maximum sum assured for the primary coverage given the primary insured's age and the
   * currency (product age bands, e.g. over 56: DOP 2,750,000). Null when no band applies.
   */
  getAgeAmountCap(currencyCode?: string | null): number | null {
    const limits = this.option?.ageAmountLimits ?? [];
    if (!limits.length) return null;
    const code = (currencyCode ?? (this.form?.get('currency')?.value as string) ?? '').toUpperCase();
    const age = this.calcAgeYears((this.form?.get('birthDate')?.value as string | null) ?? null);
    if (age == null || !code) return null;
    const caps = limits
      .filter((l) => (l.currency || '').toUpperCase() === code && age >= l.ageFrom && age <= l.ageTo)
      .map((l) => Number(l.maxAmount))
      .filter((v) => v > 0);
    return caps.length ? Math.min(...caps) : null;
  }

  private findCurrencyRange(
    list: InsuranceTypeCurrencyDto[] | null | undefined,
    code: string,
  ): { min?: number; max?: number; minAmount?: number; maxAmount?: number; isFixedLimit?: boolean } | null {
    const found = (list ?? []).find(
      (c) => (c?.currency || '').toUpperCase() === code.toUpperCase(),
    );
    if (!found) return null;
    const range: any = {};
    if (typeof found.min === 'number') range.min = found.min;
    if (typeof found.max === 'number') range.max = found.max;
    if (typeof (found as any).minAmount === 'number') range.minAmount = (found as any).minAmount;
    if (typeof (found as any).maxAmount === 'number') range.maxAmount = (found as any).maxAmount;
    if (typeof found.isFixedLimit === 'boolean') range.isFixedLimit = found.isFixedLimit;
    return Object.keys(range).length ? range : null;
  }

  private applyCoverageValidators(): void {
    const code = this.form.get('currency')?.value as string;

    // Primary coverage
    const pc = this.primaryCoverage;
    const mainCtrl = this.form.get('mainCoverage');
    if (mainCtrl) {
      // For Enfermedades Graves, Respaldo Colonial, Ultimos Gastos, or when no primary coverage exists, main coverage is not required
      if (this.isEnfermedadesGraves || this.isRespaldoColonial || this.isUltimosGastos || this.hasNoPrimaryCoverage) {
        mainCtrl.clearValidators();
        mainCtrl.updateValueAndValidity({ emitEvent: false });
      } else {
        const range = this.getCoverageRange(pc);
        if (!range) {
          // Even when no currency-specific limits, main coverage is required
          mainCtrl.setValidators([Validators.required]);
        } else {
          const isFixed = this.isCoverageFixedLimit(pc);
          const validators = [Validators.required] as any[];

          if (isFixed && typeof range.maxAmount === 'number') {
            // If fixed limit, value is strictly range.maxAmount
            validators.push(Validators.min(range.maxAmount));
            validators.push(Validators.max(range.maxAmount));
            mainCtrl.setValue(range.maxAmount, { emitEvent: false });
          } else {
            if (typeof range.minAmount === 'number')
              validators.push(Validators.min(range.minAmount));
            if (typeof range.maxAmount === 'number')
              validators.push(Validators.max(range.maxAmount));

            // Initialize with minAmount if empty or below minAmount, or cap at maxAmount
            let current = Number(mainCtrl.value ?? 0);
            if (typeof range.minAmount === 'number') {
              const min = range.minAmount;
              if (!current || current < min) {
                current = min;
                mainCtrl.setValue(current, { emitEvent: false });
              }
            }
            if (typeof range.maxAmount === 'number') {
              const max = range.maxAmount;
              if (current > max) {
                mainCtrl.setValue(max, { emitEvent: false });
              }
            }
          }
          mainCtrl.setValidators(validators);
        }
        mainCtrl.updateValueAndValidity({ emitEvent: false });
      }
    }

    this.applyAdditionalCoverageValidators();
  }

  private applyAdditionalCoverageValidators(): void {
    const code = this.form.get('currency')?.value as string;
    const mainCoverageValue = Number(this.form.get('mainCoverage')?.value ?? 0);
    const hasPrimary = !this.isEnfermedadesGraves && !this.hasNoPrimaryCoverage && mainCoverageValue > 0;

    this.additionalCoverages.forEach((cov) => {
      const amountCtrl = this.form.get(this.amountKey(cov));
      if (!amountCtrl) return;

      // If control is disabled (unselected), clear validators and return
      if (amountCtrl.disabled) {
        amountCtrl.clearValidators();
        amountCtrl.updateValueAndValidity({ emitEvent: false });
        return;
      }

      // Fraction-based coverages: auto-compute value and set as required only
      if (this.hasFraction(cov)) {
        const computed = this.computeFractionAmount(cov);
        amountCtrl.setValue(computed, { emitEvent: false });
        amountCtrl.setValidators([Validators.required]);
        amountCtrl.updateValueAndValidity({ emitEvent: false });
        return;
      }

      const range = this.findCurrencyRange(cov?.currencies, code);
      const isFixed = this.isCoverageFixedLimit(cov);
      const validators = [Validators.required] as any[];

      if (range) {
        if (isFixed && typeof range.maxAmount === 'number') {
          validators.push(Validators.min(range.maxAmount));
          validators.push(Validators.max(range.maxAmount));
          amountCtrl.setValue(range.maxAmount, { emitEvent: false });
        } else {
          if (typeof range.minAmount === 'number')
            validators.push(Validators.min(range.minAmount));

          // Cap the maxAmount at the lesser of the currency maxAmount and the main coverage value
          let effectiveMax = range.maxAmount;
          if (hasPrimary) {
            if (typeof effectiveMax === 'number') {
              effectiveMax = Math.min(effectiveMax, mainCoverageValue);
            } else {
              effectiveMax = mainCoverageValue;
            }
          }
          if (typeof effectiveMax === 'number')
            validators.push(Validators.max(effectiveMax));
        }
      } else if (hasPrimary) {
        // No currency range defined, but still cap at main coverage
        validators.push(Validators.max(mainCoverageValue));
      }

      amountCtrl.setValidators(validators);

      // If control is enabled and NOT fixed, ensure within range (fixed handled above)
      if (range && !isFixed) {
        let current = Number(amountCtrl.value ?? 0);
        if (typeof range.minAmount === 'number') {
          const min = range.minAmount;
          if (!current || current < min) {
            current = min;
            amountCtrl.setValue(current, { emitEvent: false });
          }
        }
        if (typeof range.maxAmount === 'number') {
          const max = range.maxAmount;
          if (current > max) {
            amountCtrl.setValue(max, { emitEvent: false });
          }
        }
      }
      amountCtrl.updateValueAndValidity({ emitEvent: false });
    });
  }

  private applyCoverageAgeConstraints(): void {
    const birthDateStr = this.form.get('birthDate')?.value as string | null;
    const age = this.calcAgeYears(birthDateStr);

    this.additionalCoverages.forEach((cov) => {
      const selCtrl = this.form.get(this.selectedKey(cov));
      if (!selCtrl) return;

      const isRequired = !!cov.isRequired;
      if (isRequired) return;

      const shouldDisable = age != null && typeof cov.maxAge === 'number' && age > cov.maxAge;

      if (shouldDisable) {
        if (selCtrl.value) {
          selCtrl.setValue(false, { emitEvent: true });
        }
        if (selCtrl.enabled) {
          selCtrl.disable({ emitEvent: false });
        }
      } else {
        if (!isRequired && selCtrl.disabled) {
          selCtrl.enable({ emitEvent: false });
        }
      }
    });
  }

  private handleCurrencyChange(newCurrency: string): void {
    const oldCurrency = this.previousCurrency;
    if (!oldCurrency || oldCurrency.toUpperCase() === newCurrency.toUpperCase()) return;

    const rate = this.exchangeRateService.getUsdRate();
    if (!rate || rate <= 0) {
      this.previousCurrency = newCurrency;
      return;
    }

    let multiplier = 1;
    if (oldCurrency.toUpperCase() === 'DOP' && newCurrency.toUpperCase() === 'USD') {
      multiplier = 1 / rate;
    } else if (oldCurrency.toUpperCase() === 'USD' && newCurrency.toUpperCase() === 'DOP') {
      multiplier = rate;
    } else {
      this.previousCurrency = newCurrency;
      return;
    }

    // Convert main coverage
    const mainCtrl = this.form.get('mainCoverage');
    if (mainCtrl) {
      const mainVal = Number(mainCtrl.value ?? 0);
      if (mainVal > 0) {
        const converted = Math.round((mainVal * multiplier) * 100) / 100;
        mainCtrl.setValue(converted, { emitEvent: false });
      }
    }

    // Convert additional coverages
    this.additionalCoverages.forEach((cov) => {
      const amountCtrl = this.form.get(this.amountKey(cov));
      if (amountCtrl) {
        const amountVal = Number(amountCtrl.value ?? 0);
        if (amountVal > 0) {
          const converted = Math.round((amountVal * multiplier) * 100) / 100;
          amountCtrl.setValue(converted, { emitEvent: false });
        }
      }
    });

    // Convert other selected details
    this.otherSelectedDetails.forEach((item) => {
      if (item.detail) {
        item.detail.currency = newCurrency;
        if (item.detail.coverages) {
          item.detail.coverages.forEach((c: any) => {
            if (c.currency) c.currency = newCurrency;
            if (c.amount) {
              c.amount = Math.round((Number(c.amount) * multiplier) * 100) / 100;
            }
          });
        }
      }
    });

    this.previousCurrency = newCurrency;
  }

  private validateCurrencyChange(newCurrency: string): void {
    this.currencyError = null;
    this.currencyWarning = null;
    this.form.get('currency')?.setErrors(null);

    // Currency incompatibility validation commented out:
    // Selecting another insurance type now deselects the previous one,
    // so this check is no longer needed.
    //
    // if (!this.otherSelectedDetails?.length) return;
    //
    // const incompatible = this.otherSelectedDetails.filter((item) => {
    //   const currencies = item.insuranceType.currencies || [];
    //   if (currencies.length === 0 && newCurrency.toUpperCase() === 'DOP') return false;
    //   const allowed = currencies.map((c) => c?.currency?.toUpperCase());
    //   return !allowed.includes(newCurrency.toUpperCase());
    // });
    //
    // if (incompatible.length > 0) {
    //   const names = incompatible.map((i) => i.insuranceType.name).join(', ');
    //   this.currencyError = `La acción no es posible ya que el seguro ${names} no tiene la moneda ${newCurrency.toUpperCase() === 'USD' ? 'US$' : 'RD$'
    //     }.`;
    //   this.form.get('currency')?.setErrors({ incompatibleCurrency: true });
    // }
  }

  /** Re-runs the age-dependent birth date validators; call whenever duration changes. */
  private revalidateAgeDependentControls(): void {
    this.form.get('birthDate')?.updateValueAndValidity({ emitEvent: false });
    this.form.get('spouseBirthDate')?.updateValueAndValidity({ emitEvent: false });
    this.childrenArray?.controls?.forEach((grp) =>
      grp.get('birthDate')?.updateValueAndValidity({ emitEvent: false }),
    );
  }

  /**
   * The primary insured's BMI (see `computeBmi`). Reads from the given control
   * (defaults to the live form) rather than always `this.form` so it also works
   * when invoked from the group validator during construction, before `this.form`
   * has been assigned.
   */
  private computeBmi(source: AbstractControl | undefined = this.form): number | null {
    const weightRaw = Number(source?.get('weight')?.value);
    const unit = (source?.get('weightUnit')?.value as string) ?? 'lb';
    const weightLb = unit === 'kg' ? weightRaw * KG_TO_LB : weightRaw;
    return computeBmiFromImperial(weightLb, source?.get('heightFeet')?.value, source?.get('heightInches')?.value);
  }

  /** The primary insured's current BMI, or null until weight and height are filled in. */
  get bmiValue(): number | null {
    return this.computeBmi();
  }

  /** Whether the primary insured's BMI exceeds the maximum allowed to proceed. */
  get bmiExceedsMax(): boolean {
    const bmi = this.bmiValue;
    return bmi != null && bmi > MAX_BMI;
  }

  private bmiValidator(group: AbstractControl) {
    const bmi = this.computeBmi(group);
    return bmi != null && bmi > MAX_BMI ? { bmiExceeded: true } : null;
  }

  /**
   * The max-duration-age limit (in years) that some insured would exceed at the end of the
   * chosen duration, or null when everyone fits. Covers the primary insured, spouse, children
   * (child-specific limit) and other insureds. When a birth date is within range, exceeding this
   * is a duration problem, so it's shown under the duration field rather than on a birth date.
   */
  get durationExceededMaxAgeLimit(): number | null {
    const form = this.form;
    if (!form) return null;
    const years = Number(form.get('duration')?.value ?? 0) / 12;
    const exceeds = (birthDate: string | null | undefined, limit: number | null | undefined): boolean => {
      if (!limit || limit <= 0) return false;
      const age = this.calcAgeYears(birthDate ?? null);
      return age != null && age + years > limit;
    };

    const adultLimit = this.option?.maxDurationAge;
    if (exceeds(form.get('birthDate')?.value, adultLimit)) return adultLimit!;
    if (form.get('spouseIncluded')?.value && exceeds(form.get('spouseBirthDate')?.value, adultLimit)) {
      return adultLimit!;
    }
    if (form.get('childrenEnabled')?.value) {
      const childLimit = this.option?.childMaxDurationAge ?? adultLimit;
      if (this.childrenArray?.controls?.some((c) => exceeds(c.get('birthDate')?.value, childLimit))) {
        return childLimit!;
      }
    }
    if (form.get('othersEnabled')?.value) {
      if (this.othersArray?.controls?.some((o) => exceeds(o.get('birthDate')?.value, adultLimit))) {
        return adultLimit!;
      }
    }
    return null;
  }

  get durationExceedsMaxAge(): boolean {
    return this.durationExceededMaxAgeLimit != null;
  }

  private maxDurationAgeValidator(group: AbstractControl) {
    const minAge = this.option?.minAge;
    const maxAge = this.option?.maxAge;
    const maxDurationAge = this.option?.maxDurationAge;
    const durationMonths = Number(group.get('duration')?.value ?? 0);

    // Helper to validate one birthDate
    const validateOne = (
      birthDateStr: string | null | undefined,
      customMinAge?: number | null,
      customMaxAge?: number | null,
      customMaxDurationAge?: number | null,
    ): string | null => {
      if (!birthDateStr) return null; // allow empty unless separately required
      const age = this.calcAgeYears(birthDateStr);
      if (age == null) return 'invalidBirthDate';
      const effectiveMin = customMinAge ?? minAge;
      const effectiveMax = customMaxAge ?? maxAge;
      const effectiveMaxDurAge = customMaxDurationAge ?? maxDurationAge;
      if (typeof effectiveMin === 'number' && age < effectiveMin) return 'minAge';
      if (typeof effectiveMax === 'number' && age > effectiveMax) return 'maxAge';
      if (effectiveMaxDurAge && effectiveMaxDurAge > 0) {
        const futureAge = age + durationMonths / 12;
        if (futureAge > effectiveMaxDurAge) return 'maxDurationAgeExceeded';
      }
      return null;
    };

    // Primary insured
    const primaryErr = validateOne(group.get('birthDate')?.value as string);
    if (primaryErr) return { [primaryErr]: true };

    // Spouse when included: require and validate
    const spouseIncluded = !!group.get('spouseIncluded')?.value;
    if (spouseIncluded) {
      const spouseBDCtrl = group.get('spouseBirthDate');
      const spouseBD = spouseBDCtrl?.value as string | null;
      if (!spouseBD) return { spouseBirthDateRequired: true };
      const spouseMinAge = Math.max(18, this.option?.minAge ?? 0);
      const se = validateOne(spouseBD, spouseMinAge);
      if (se) return { spouseBirthDateInvalid: true, [se]: true } as any;
    }

    // Children when enabled: require and validate each with child-specific limits
    const childrenEnabled = !!group.get('childrenEnabled')?.value;
    if (childrenEnabled) {
      const childMinAge = this.option?.childMinAge;
      const childMaxAge = this.option?.childMaxAge;
      const childMaxDurationAge = this.option?.childMaxDurationAge;
      const children = group.get('children') as any;
      const arr: AbstractControl[] = (children?.controls ??
        []) as AbstractControl[];
      for (const child of arr) {
        const bdCtrl = child.get('birthDate');
        const cbd = bdCtrl?.value as string | null;
        if (!cbd) return { childBirthDateRequired: true };
        const ce = validateOne(cbd, childMinAge, childMaxAge, childMaxDurationAge);
        if (ce) {
          // Also set error on the child's birthDate control so the input turns red, except for
          // maxDurationAgeExceeded, which is a duration problem shown under the duration field.
          if (ce !== 'maxDurationAgeExceeded') {
            bdCtrl?.setErrors({ [ce]: true });
            bdCtrl?.markAsTouched();
          }
          return { childBirthDateInvalid: true, [ce]: true } as any;
        }
      }
    }

    // Other insureds: only the end-of-term age is checked here; their own age range is
    // validated on each birth date control (validateOtherBirthDate).
    const othersEnabled = !!group.get('othersEnabled')?.value;
    if (othersEnabled && maxDurationAge && maxDurationAge > 0) {
      const others = group.get('others') as any;
      const arr: AbstractControl[] = (others?.controls ?? []) as AbstractControl[];
      for (const other of arr) {
        const age = this.calcAgeYears(other.get('birthDate')?.value as string | null);
        if (age != null && age + durationMonths / 12 > maxDurationAge) {
          return { otherBirthDateInvalid: true, maxDurationAgeExceeded: true } as any;
        }
      }
    }

    return null;
  }

  private validatePrimaryBirthDate = (control: AbstractControl) => {
    const birthDateStr = control.value as string | null;
    if (!birthDateStr) return null; // not required by default

    const age = this.calcAgeYears(birthDateStr);
    if (age == null) return { invalidBirthDate: true };

    const minAge = this.option?.minAge;
    const maxAge = this.option?.maxAge;

    if (typeof minAge === 'number' && age < minAge) return { minAge: true };
    if (typeof maxAge === 'number' && age > maxAge) return { maxAge: true };

    // maxDurationAge is deliberately not checked here: when the birth date itself is
    // within range, exceeding it is a duration problem, not a birth date problem, and
    // is surfaced under the duration field instead (see durationExceedsMaxAge).

    return null;
  };

  private validateSpouseBirthDate = (control: AbstractControl) => {
    const birthDateStr = control.value as string | null;
    if (!birthDateStr) return null;

    const age = this.calcAgeYears(birthDateStr);
    if (age == null) return { invalidBirthDate: true };

    const minAge = Math.max(18, this.option?.minAge ?? 0);
    const maxAge = this.option?.maxAge;

    if (age < minAge) return { minAge: true };
    if (typeof maxAge === 'number' && age > maxAge) return { maxAge: true };

    // maxDurationAge is checked by the form-level validator and shown under the duration field.

    return null;
  };

  private validateChildBirthDate = (control: AbstractControl) => {
    const birthDateStr = control.value as string | null;
    if (!birthDateStr) return null;

    const age = this.calcAgeYears(birthDateStr);
    if (age == null) return { invalidBirthDate: true };

    const childBd = this.parseBirthDate(birthDateStr);
    const primaryBirthDateStr = this.form?.get('birthDate')?.value as string | null;
    if (childBd && primaryBirthDateStr) {
      const primaryBd = this.parseBirthDate(primaryBirthDateStr);
      if (primaryBd) {
        const diffYears = this.getYearDiff(primaryBd, childBd);
        if (diffYears < 13) {
          return { minParentAgeDiff: true };
        }
      }
    }

    const spouseIncluded = !!this.form?.get('spouseIncluded')?.value;
    const spouseBirthDateStr = this.form?.get('spouseBirthDate')?.value as string | null;
    if (childBd && spouseIncluded && spouseBirthDateStr) {
      const spouseBd = this.parseBirthDate(spouseBirthDateStr);
      if (spouseBd) {
        const diffYears = this.getYearDiff(spouseBd, childBd);
        if (diffYears < 13) {
          return { minSpouseAgeDiff: true };
        }
      }
    }

    // Use child-specific age limits, falling back to primary limits
    const minAge = this.option?.childMinAge ?? this.option?.minAge;
    const maxAge = this.option?.childMaxAge ?? this.option?.maxAge;

    if (typeof minAge === 'number' && age < minAge) return { minAge: true };
    if (typeof maxAge === 'number' && age > maxAge) return { maxAge: true };

    // maxDurationAge is checked by the form-level validator and shown under the duration field.

    return null;
  };

  private validateOtherBirthDate = (control: AbstractControl) => {
    const birthDateStr = control.value as string | null;
    if (!birthDateStr) return null;

    const age = this.calcAgeYears(birthDateStr);
    if (age == null) return { invalidBirthDate: true };

    // Adult age validation (same as spouse)
    const minAge = Math.max(18, this.option?.minAge ?? 0);
    const maxAge = this.option?.maxAge;

    if (age < minAge) return { minAge: true };
    if (typeof maxAge === 'number' && age > maxAge) return { maxAge: true };

    // maxDurationAge is checked by the form-level validator and shown under the duration field.

    return null;
  };

  private parseBirthDate(birthDateStr: string | null): Date | null {
    return parseDateString(birthDateStr);
  }

  private calcAgeYears(birthDateStr: string | null): number | null {
    return calcAgeYears(birthDateStr);
  }

  private getYearDiff(older: Date, younger: Date): number {
    let diffYears = younger.getFullYear() - older.getFullYear();
    const m = younger.getMonth() - older.getMonth();
    if (m < 0 || (m === 0 && younger.getDate() < older.getDate())) {
      diffYears--;
    }
    return diffYears;
  }

  private async ensurePopovers(): Promise<void> {
    if (typeof window === 'undefined') return;

    const buttons = this.infoButtons ?? [];
    const elements = new Set<HTMLElement>();
    buttons.forEach((b: ElementRef<HTMLButtonElement>) =>
      elements.add(b.nativeElement),
    );

    if (!elements.size) {
      this.detachPopovers();
      return;
    }

    if (!this.popoverCtor) {
      const { default: Popover } = await import('bootstrap/js/dist/popover.js');
      this.popoverCtor = Popover;
    }

    this.popovers.forEach((popover, element) => {
      if (!elements.has(element)) {
        popover.dispose();
        this.popovers.delete(element);
      }
    });

    elements.forEach((element) => {
      if (this.popovers.has(element) || !this.popoverCtor) return;
      const title = element.dataset['infoTitle'] ?? '';
      const content = formatDescription(element.dataset['infoContent'] ?? '');
      const popover = new this.popoverCtor(element, {
        trigger: 'click',
        title,
        content,
        container: 'body',
        html: true,
        customClass: 'custom-popover',
      });
      this.popovers.set(element, popover);
    });
  }

  private detachPopovers(): void {
    this.popovers.forEach((p) => p.dispose());
    this.popovers.clear();
  }

  private hideOpenPopovers(except?: HTMLElement): void {
    this.popovers.forEach((popover, element) => {
      if (except && element === except) return;
      popover.hide();
    });
  }
}
